import type { App } from 'vue';

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { globalState, simpleObservableStore } from 'boot/simpleObservableStore';
import { useAuthenticationStore } from 'src/stores/authentication';

import { userService } from 'src/services/authentication/user.service';

import useAuthentication from 'src/filters/authentication';

import {
	appCompatibilityMismatchFromHeader,
	responseContainsRolloutCompatibleLegacyToken,
} from 'src/util/app/appCompatibility';
import { EventBus } from 'src/util/eventBus/EventBus';
import { isIos } from 'src/util/platform';

import {
	authDeniedReasonAfterUnauthorizedResponses,
	endAppSessionForAuthDeniedReason,
	loginHashForAuthDeniedReason,
	unauthorizedRequestIsRetryable,
} from './axiosAuthDeniedReason';
import { appAuthenticationHeadersAreEnabledForRequest } from './axiosAuthenticationHeaders';
import { decodeCompressedResponseData } from './axiosCompressedResponse';

declare module 'axios' {
	interface AxiosRequestConfig {
		__authRetryAttempted?: boolean;
		skipAppAuthenticationHeaders?: boolean;
		skipUnauthorizedLogout?: boolean;
	}
}

declare module '@vue/runtime-core' {
	interface ComponentCustomProperties {
		$axios: AxiosInstance;
	}
}

function shouldLogoutForUnauthorizedResponse(error: unknown): boolean {
	return axios.isAxiosError(error) && error.response?.status === 401 && error.config?.skipUnauthorizedLogout !== true;
}

function configForUnauthorizedLogout(error: unknown): AxiosRequestConfig | undefined {
	if (!shouldLogoutForUnauthorizedResponse(error) || !axios.isAxiosError(error)) {
		return undefined;
	}

	return error.config;
}

function updateVersionMismatchFromCompatibilityHeader(headerValue: unknown): void {
	const responseAppCompatibilityMismatch = appCompatibilityMismatchFromHeader(
		process.env.APP_COMPATIBILITY,
		headerValue
	);
	const responseUsesRolloutCompatibleLegacyBackend = responseContainsRolloutCompatibleLegacyToken(
		process.env.APP_COMPATIBILITY,
		headerValue
	);

	if (responseAppCompatibilityMismatch !== null) {
		simpleObservableStore.showVersionMismatch = responseAppCompatibilityMismatch;
		simpleObservableStore.usesRolloutCompatibleLegacyBackend = responseUsesRolloutCompatibleLegacyBackend;
	}
}

axios.interceptors.request.use((config) => {
	const { authenticationToken } = useAuthentication();

	if (appAuthenticationHeadersAreEnabledForRequest(config)) {
		if (config.headers) {
			if (globalState.authToken !== '') {
				config.headers['Authorization'] = `Bearer ${globalState.authToken}`;
				config.headers['X-Platform'] = 'App';
			} else if ((authenticationToken.value as string) !== '') {
				config.headers['Authorization'] = `Bearer ${authenticationToken.value as string}`;
				config.headers['X-Platform'] = 'App';
				config.headers['x-csrf-token'] = globalState.csrfToken;
			} else {
				config.headers['X-Platform'] = 'App';
			}
		}
	}

	return config;
});

axios.interceptors.response.use(
	function (successResponse) {
		updateVersionMismatchFromCompatibilityHeader(successResponse.headers['x-appcompatibility']);

		if (successResponse.headers.jwetoken !== undefined) {
			const { updateTheAuthenticationToken } = useAuthentication();

			updateTheAuthenticationToken(String(successResponse.headers.jwetoken));
		}

		if (successResponse.headers['content-type'] == 'application/x-compress') {
			successResponse.data = decodeCompressedResponseData(successResponse.data);
		}
		return successResponse;
	},
	async (error) => {
		if (axios.isAxiosError(error)) {
			updateVersionMismatchFromCompatibilityHeader(error.response?.headers['x-appcompatibility']);
		}

		if (simpleObservableStore.showVersionMismatch) {
			throw error;
		}

		const requestConfig = configForUnauthorizedLogout(error);

		if (requestConfig !== undefined) {
			if (requestConfig.__authRetryAttempted === true) {
				throw error;
			}

			const method = requestConfig.method?.toUpperCase();
			const unauthorizedResponse = axios.isAxiosError(error) ? error.response : undefined;
			let retryResponse: AxiosResponse | undefined;
			if (unauthorizedRequestIsRetryable(method, unauthorizedResponse)) {
				requestConfig.__authRetryAttempted = true;
				try {
					return await axios.request(requestConfig);
				} catch (retryErr: unknown) {
					if (!axios.isAxiosError(retryErr) || retryErr.response?.status !== 401) {
						throw retryErr;
					}
					retryResponse = retryErr.response;
				}
			}

			console.warn(`[auth] 401 confirmed on ${method ?? 'unknown'} ${requestConfig.url ?? ''} — logging out`);
			simpleObservableStore.bootHealth = 'authLost';

			const { activeInstanceAuthenticationConfiguration, activeInstanceAuthenticationConfigurationName } =
				useAuthentication();

			// Stored before the session ends: ending it flips loggedIn, and App.vue's logout reload reads the reason.
			simpleObservableStore.authDeniedReason = authDeniedReasonAfterUnauthorizedResponses(
				[unauthorizedResponse, retryResponse],
				simpleObservableStore.authDeniedReason
			);
			const authDeniedReason = simpleObservableStore.authDeniedReason;

			const eventBus: EventBus = globalState.eventBus as EventBus;
			await endAppSessionForAuthDeniedReason(authDeniedReason, {
				// Like authenticationStore.logout() without the POST: loggedIn=false lets App.vue reload onto the login
				// page with the reason.
				endLocalSession: () =>
					userService.endLocalSession(activeInstanceAuthenticationConfigurationName.value, eventBus),
				logout: () =>
					userService.logout(
						activeInstanceAuthenticationConfigurationName.value,
						activeInstanceAuthenticationConfiguration.value?.user as string,
						eventBus
					),
				markLoggedOut: () => useAuthenticationStore().logoutState(),
			});

			const loginHash = loginHashForAuthDeniedReason(authDeniedReason);
			if (typeof window !== 'undefined' && !window.location.hash.startsWith(loginHash)) {
				window.location.assign(window.location.pathname + window.location.search + loginHash);
			}
		}

		throw error;
	}
);

export function bootAxios({ app }: { app: App<Element> }): void {
	axios.defaults.withCredentials = !isIos();
	app.config.globalProperties.$axios = axios;
}
